import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchOrganizationSurveysPayload,
  normalizeOrganizationSurveys,
} from "../organization/organizationSurveysApi";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";

const getOrganizationSurveyId = (survey) => survey?.organization_survey_id ?? survey?.id ?? null;

const ConsultantResultsPage = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchOrganizationSurveysPayload({ organizationId });
      setSurveys(
        normalizeOrganizationSurveys(payload)
          .map((survey) => ({
            ...survey,
            organization_survey_id: getOrganizationSurveyId(survey),
          }))
          .filter((survey) => survey.organization_survey_id)
      );
    } catch (err) {
      setError("Could not load consultant results.");
      toast({ title: "Could not load consultant results", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [organizationId, toast]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const sortedSurveys = useMemo(
    () => [...surveys].sort((a, b) => formatOrganizationSurveyTitle(a).localeCompare(formatOrganizationSurveyTitle(b))),
    [surveys]
  );

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg">Results</Heading>
        <Text color="gray.600" mt={2}>
          Choose a survey cycle to open the full report for this organization.
        </Text>
      </Box>

      {sortedSurveys.length === 0 ? (
        <Card>
          <CardBody>
            <Text color="gray.600">No reports are available for this school yet.</Text>
          </CardBody>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {sortedSurveys.map((survey) => (
            <Card key={survey.organization_survey_id}>
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Heading size="md">{formatOrganizationSurveyTitle(survey)}</Heading>
                  <Text color="gray.600">
                    Open the full report to review overview, strengths, development, and supporting sections.
                  </Text>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `/app/consultant/organizations/${organizationId}/results/${survey.organization_survey_id}`,
                        {
                          state: {
                            surveyTitle: formatOrganizationSurveyTitle(survey),
                            survey,
                          },
                        }
                      )
                    }
                  >
                    View full results
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default ConsultantResultsPage;
